// useAccess (Phase: admin/role-based-access §1) — the tier-aware read path for UI gating.
//
// Thin wrapper over `usePermissions().can()`. All the decision logic is pure in `@/auth/tiers`;
// this binds it to the current session. New gating should go through this or <Gate>, never a raw
// `user.isAdmin` flag (which is the platform-staff bit and NOT the org-admin one — see the
// `isAdmin` collision note in Phases/admin/PHASE-ROLE-BASED-ACCESS.md).
//
// Authentication is enforced HERE, not in accessAllows: `access: 'user'` means "any authenticated
// user", and since it maps to no permission key the pure function cannot distinguish a member from
// a signed-out visitor. Checking it once at this seam keeps every caller correct.
import { useAuthStore } from '@/store/authStore';
import { usePermissions } from '@/hooks/usePermissions';
import { accessAllows, resolveTier, type AccessSpec, type Tier } from '@/auth/tiers';

export interface UseAccess {
  /** Whether the current user may see something declaring `spec`, minus any denied tiers. */
  allows: (spec: AccessSpec, deny?: Tier[]) => boolean;
  /** The highest tier the current user holds. `user` when signed out — pair with `allows`. */
  tier: Tier;
}

export function useAccess(): UseAccess {
  const { can } = usePermissions();
  const currentUser = useAuthStore((s) => s.currentUser);
  const authenticated = currentUser !== null;

  return {
    allows: (spec: AccessSpec, deny?: Tier[]) => authenticated && accessAllows(can, spec, deny),
    tier: resolveTier(can),
  };
}
